import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Skeleton,
  CircularProgress,
  TextField,
} from "@mui/material";
import { LineChart, BarChart } from "@mui/x-charts";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useGetDashboardPriceStatsQuery } from "store/services/dashboardApi";
import { useNavigate } from "react-router-dom";
import { useAuth } from "hooks";
import moment from "moment";
import { ChartTypeToggle, ChartValueMarkElement, TopBarLabel, barValueLabel, formatCompactNumber, ZoomableBox, downloadChartSvg } from "./ChartControls";

const ShipmentPriceChart = () => {
  const { isEntityAdmin, isAccountAdmin, isSystemAdmin } = useAuth();
  const chartRef = useRef(null);
  const theme = useTheme();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [chartType, setChartType] = useState("bar");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const menuTimeout = useRef(null);
  const navigate = useNavigate();
  const isDeviceLandscape = useMediaQuery(
    "(orientation: landscape) and (max-height: 650px), (orientation: landscape) and (hover: none) and (pointer: coarse) and (max-width: 1200px)"
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const chartMargin = isMobile
    ? { left: 42, right: 20, top: 46, bottom: 78 }
    : { left: 58, right: 48, top: 46, bottom: 78 };

  const [timePeriod, setTimePeriod] = useState("week");
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);

  const getRange = (period, from, to) => {
    if (period === "custom" && from && to) {
      return {
        from: moment(from).format("YYYY-MM-DD"),
        to: moment(to).format("YYYY-MM-DD"),
      };
    }
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - (period === "month" ? 29 : 6));
    return {
      from: moment(start).format("YYYY-MM-DD"),
      to: moment(today).format("YYYY-MM-DD"),
    };
  };

  const [dateRange, setDateRange] = useState(() => getRange("week"));

  useEffect(() => {
    if (timePeriod === "custom" && !(customFrom && customTo)) return;
    setDateRange(getRange(timePeriod, customFrom, customTo));
  }, [timePeriod, customFrom, customTo]);

  const maxToDate = React.useMemo(() => {
    if (timePeriod === "custom" && customFrom) {
      const max = new Date(customFrom);
      max.setMonth(max.getMonth() + 3);
      return max < new Date() ? max : new Date();
    }
    return new Date();
  }, [timePeriod, customFrom]);

  const minFromDate = React.useMemo(() => {
    if (timePeriod === "custom" && customTo) {
      const min = new Date(customTo);
      min.setMonth(min.getMonth() - 3);
      return min;
    }
    return undefined;
  }, [timePeriod, customTo]);

  const periodLabel =
    timePeriod === "week"
      ? "Last 7 Days"
      : timePeriod === "month"
        ? "Last 30 Days"
        : "Custom Range";

  const {
    data: priceStats = [],
    isLoading,
    isFetching,
    isError,
  } = useGetDashboardPriceStatsQuery(
    { fromDate: dateRange.from, toDate: dateRange.to },
    {
      refetchOnMountOrArgChange: false,
      keepUnusedDataFor: 300,
    }
  );

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    clearTimeout(menuTimeout.current);
    menuTimeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, 3000);
  };

  const handleMenuClose = () => {
    clearTimeout(menuTimeout.current);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (open) {
      clearTimeout(menuTimeout.current);
      menuTimeout.current = setTimeout(() => {
        setAnchorEl(null);
      }, 5000);
    }
    return () => clearTimeout(menuTimeout.current);
  }, [open]);

  const handleFullScreen = () => {
    setIsFullScreen(true);
    handleMenuClose();
  };

  const handleMinimize = () => {
    setIsFullScreen(false);
  };

  useEffect(() => {
    const exitHandler = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        setIsFullScreen(false);
        try {
          if (window.screen?.orientation?.unlock) {
            window.screen.orientation.unlock();
          }
        } catch (e) {}
      }
    };
    document.addEventListener("fullscreenchange", exitHandler);
    document.addEventListener("webkitfullscreenchange", exitHandler);
    return () => {
      document.removeEventListener("fullscreenchange", exitHandler);
      document.removeEventListener("webkitfullscreenchange", exitHandler);
    };
  }, []);

  useEffect(() => {
    if (isFullScreen) {
      const scrollY = window.scrollY;
      const originalBody = document.body.style.cssText;
      const originalHtml = document.documentElement.style.cssText;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.documentElement.style.overflow = "hidden";
      return () => {
        document.body.style.cssText = originalBody;
        document.documentElement.style.cssText = originalHtml;
        window.scrollTo(0, scrollY);
      };
    }
  }, [isFullScreen]);

  const handleDownload = (format) => {
    downloadChartSvg(chartRef.current, format, "shipment_price_chart");
    handleMenuClose();
  };

  const handleViewDetails = () => {
    navigate("/app/crm/shipment/price-charts");
    handleMenuClose();
  };

  const dates = priceStats.map((entry) =>
    new Date(entry.date).toLocaleDateString("en-US", { month: "short", day: "2-digit" })
  );
  const totalCharges = priceStats.map((entry) => Math.round(entry.totalCharge));

  return (
    <Box
      ref={chartRef}
      sx={{
        mt: 2,
        width: isFullScreen ? "100vw" : "100%",
        height: isFullScreen ? "100vh" : "auto",
        position: isFullScreen ? "fixed" : "relative",
        ...(isFullScreen && {
          top: 0,
          left: 0,
          zIndex: 1300,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }),
        padding: 2,
        boxShadow: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={{ xs: 1, sm: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          {isFullScreen && (
            <IconButton onClick={handleMinimize} aria-label="Back">
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="p" sx={{ fontWeight: "bold", color: theme.palette.primary.main }}>
            Shipment Charges ({periodLabel})
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <ChartTypeToggle
            value={chartType}
            onChange={(_, value) => value && setChartType(value)}
          />
          <IconButton onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            slotProps={{ paper: { sx: { zIndex: 1400 } } }}
          >
            {(isEntityAdmin || isAccountAdmin || isSystemAdmin) && (
              <MenuItem
                onClick={handleViewDetails}
                sx={{ display: isFullScreen ? "none" : "flex" }}
              >
                View Details
              </MenuItem>
            )}
            <MenuItem
              onClick={handleFullScreen}
              sx={{ display: isFullScreen ? "none" : "flex" }}
            >
              View Full Screen
            </MenuItem>
            <MenuItem onClick={() => handleDownload("png")}>
              <GetAppIcon sx={{ mr: 1 }} /> Download PNG
            </MenuItem>
            <MenuItem onClick={() => handleDownload("jpeg")}>
              <GetAppIcon sx={{ mr: 1 }} /> Download JPEG
            </MenuItem>
            <MenuItem onClick={() => handleDownload("pdf")}>
              <PictureAsPdfIcon sx={{ mr: 1 }} /> Download PDF
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {isFullScreen && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
            <TextField
              select
              size="small"
              SelectProps={{
                native: true,
                MenuProps: { container: chartRef.current },
              }}
              label="Time Period"
              value={timePeriod}
              onChange={(e) => setTimePeriod(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="custom">Custom Date Range</option>
            </TextField>
            {timePeriod === "custom" && (
              <>
                <DatePicker
                  label="From Date"
                  value={customFrom}
                  minDate={minFromDate}
                  maxDate={customTo || maxToDate}
                  onChange={(newValue) => setCustomFrom(newValue)}
                  PopperProps={{
                    sx: { zIndex: 1500 },
                  }}
                  renderInput={(params) => <TextField {...params} size="small" />}
                />
                <DatePicker
                  label="To Date"
                  value={customTo}
                  minDate={customFrom || minFromDate}
                  maxDate={maxToDate}
                  onChange={(newValue) => setCustomTo(newValue)}
                  PopperProps={{
                    sx: { zIndex: 1500 },
                  }}
                  renderInput={(params) => <TextField {...params} size="small" />}
                />
              </>
            )}
          </Box>
        </LocalizationProvider>
      )}

      <Box sx={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: isFullScreen ? (isDeviceLandscape ? "calc(100vh - 150px)" : "calc(100vh - 200px)") : { xs: 285, sm: 320 }, minHeight: isFullScreen ? (isDeviceLandscape ? "calc(100vh - 150px)" : "calc(100vh - 200px)") : { xs: 285, sm: 320 } }}>
        {isLoading ? (
          <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 6 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="rectangular" width="90%" height={220} sx={{ borderRadius: 1 }} />
            <CircularProgress size={20} />
          </Box>
        ) : (
          <>
            <ZoomableBox
              enabled={isFullScreen}
              sx={{ width: "100%", height: "100%" }}
            >
              {chartType === "bar" ? (
                <BarChart
                  sx={{ width: "100%", height: "100%" }}
                  xAxis={[
                    {
                      scaleType: "band",
                      data: dates,
                      tickLabelStyle: {
                        angle: -45,
                        textAnchor: "end",
                        fontSize: isMobile ? 9 : 11,
                      },
                    },
                  ]}
                  margin={chartMargin}
                  barLabel={barValueLabel}
                  slots={{ barLabel: TopBarLabel }}
                  yAxis={[
                    {
                      min: 0,
                      max: Math.ceil(Math.max(...totalCharges, 100) * 1.25),
                      valueFormatter: formatCompactNumber,
                    },
                  ]}
                  series={[
                    {
                      data: totalCharges,
                      label: "Total Charges",
                      color: "rgba(0, 128, 255, 0.7)",
                    },
                  ]}
                />
              ) : (
                <LineChart
                  sx={{ width: "100%", height: "100%" }}
                  xAxis={[
                    {
                      scaleType: "point",
                      data: dates,
                      tickLabelStyle: {
                        angle: -45,
                        textAnchor: "end",
                        fontSize: isMobile ? 9 : 11,
                      },
                    },
                  ]}
                  margin={chartMargin}
                  yAxis={[
                    {
                      min: 0,
                      max: Math.ceil(Math.max(...totalCharges, 100) * 1.25),
                      valueFormatter: formatCompactNumber,
                    },
                  ]}
                  series={[
                    {
                      data: totalCharges,
                      label: "Total Charges",
                      color: "rgba(0, 128, 255, 0.7)",
                      strokeWidth: 3,
                      showMark: true,
                    },
                  ]}
                  slots={{ mark: ChartValueMarkElement }}
                />
              )}
            </ZoomableBox>
        {isFetching && (
          <CircularProgress
            size={18}
            sx={{ position: "absolute", top: 8, right: 8 }}
          />
        )}
      </>
      )}
      {isError && (
        <Typography variant="body2" color="error" sx={{ position: "absolute" }}>
          Failed to load data
        </Typography>
      )}
      </Box>
    </Box>
  );
};

export default ShipmentPriceChart;
